import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, RadioList, Select } from '@treezor/design-system';

import { EParentType } from '@/features/user/store/user.model';
import {
  CreateUserTaxResidences,
  defaultTaxResidence,
} from '@/features/user/components/CreateUser/CreateUserTaxResidences';
import { USResident } from '@/features/user/components/CreateUser/USResident';
import { Section, TwoColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { useCreateBusinessUser } from '@/features/user/hooks/createUser/useCreateBusinessUser';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { ICreateUserInfo, ITaxResidenceCreate } from '@/features/user/contexts/CreateUserContext';

interface ISituationAndTaxResidenceProps {
  memberIndex: number;
  memberWatch: ICreateUserInfo;
  handleChange: (
    memberIndex: number,
    key: keyof ICreateUserInfo,
    value: string | number | ITaxResidenceCreate[],
  ) => void;
  handleChangeMemberProps?: (
    index: number,
    props: {
      key: keyof ICreateUserInfo;
      value: string | ITaxResidenceCreate[];
    }[],
  ) => void;
}

export function SituationAndTaxResidence({
  memberIndex,
  memberWatch,
  handleChange: handleChangeMember,
  handleChangeMemberProps,
}: ISituationAndTaxResidenceProps) {
  const { t } = useTranslation();

  const { config, user } = useCreateBusinessUser();

  const {
    personalAssetOptions,
    incomeRangeOptions,
    occupationTypeOptions,
    occupationCategoryOptions,
    personalAssetRanges,
    monthlyIncomeRangeOptions,
  } = useFieldOptions();

  const handleChange = useCallback(
    (field: keyof ICreateUserInfo, value: string | number | ITaxResidenceCreate[]) => {
      handleChangeMember(memberIndex, field, value);
    },
    [handleChangeMember, memberIndex],
  );

  const shouldBeUSResident = useMemo(
    () =>
      ['country', 'secondaryCountry', 'nationality', 'birthCountry', 'nationalityOther'].some(
        (key: keyof ICreateUserInfo) => user?.members?.[memberIndex]?.[key] === 'US' || memberWatch?.[key] === 'US',
      ),
    [user?.members, memberIndex, memberWatch],
  );

  return (
    <div className="flex flex-col gap-6">
      <Section title={t('pages.dashboard.createUser.form.blocks.situation')} key={`situation-${memberIndex}`}>
        {config?.settings?.users?.dataQuality?.includes('personalSituation') ? (
          <>
            <Select
              label={t('pages.dashboard.createUser.form.occupation.label')}
              options={occupationCategoryOptions}
              defaultValue={memberWatch?.occupationCategory}
              onChange={(value: number) => handleChange('occupationCategory', value)}
              noOptionsComponent={<CreateUserEmptyComponent />}
              tooltipInfo={{
                content: t('pages.dashboard.createUser.form.occupation.tooltip'),
                className: 'min-w-[140px]',
              }}
              withSearch
              showDelete
            />
            <TwoColumns className="items-end">
              <Select
                label={t('pages.dashboard.createUser.form.personalAssetsEstimated.label')}
                options={personalAssetRanges}
                defaultValue={memberWatch?.personalAssetsRange}
                onChange={(value: number) => handleChange('personalAssetsRange', value)}
                noOptionsComponent={<CreateUserEmptyComponent />}
                withSearch
                tooltipInfo={{
                  content: t('pages.dashboard.createUser.form.personalAssetsEstimated.tooltip'),
                  className: 'min-w-[240px]',
                }}
                showDelete
              />
              <Select
                label={t('pages.dashboard.createUser.form.netMontlyIncome.label')}
                options={monthlyIncomeRangeOptions}
                defaultValue={memberWatch?.monthlyIncomeRange}
                onChange={(value: number) => handleChange('monthlyIncomeRange', value)}
                noOptionsComponent={<CreateUserEmptyComponent />}
                withSearch
                showDelete
              />
            </TwoColumns>
          </>
        ) : (
          <>
            <TwoColumns>
              <RadioList
                label={t('pages.dashboard.createUser.form.personalAssets.title')}
                options={personalAssetOptions}
                value={memberWatch?.personalAssets}
                onChange={(value: string) => handleChange('personalAssets', value)}
                className="pt-2 gap-3"
              />
              <RadioList
                label={t('pages.dashboard.createUser.form.incomeRange.title')}
                options={incomeRangeOptions}
                value={memberWatch?.incomeRange}
                onChange={(value: string) => handleChange('incomeRange', value)}
                className="pt-2 gap-3"
              />
            </TwoColumns>
            <RadioList
              label={t('pages.currentProfiles.profile.financial.occupationType')}
              options={occupationTypeOptions}
              value={memberWatch?.occupationType}
              onChange={(value: string) => handleChange('occupationType', value)}
              className="pt-2 gap-3"
            />
          </>
        )}
      </Section>

      <Section title={t('pages.currentProfiles.profile.taxResidence.cardTitle')} key={`tax-residence-${memberIndex}`}>
        <USResident
          title={t('pages.dashboard.createUser.form.isUsTaxResident')}
          description={t('pages.dashboard.createUser.form.usTaxResidentDescription')}
          value={memberWatch?.specifiedUSPerson.toString()}
          handleValuesChange={(changes) => handleChangeMemberProps(memberIndex, changes)}
          taxResidences={memberWatch?.taxResidences}
          shouldShowWarning={shouldBeUSResident && memberWatch?.specifiedUSPerson.toString() !== '1'}
        />

        {memberWatch.taxResidences?.length ? (
          <CreateUserTaxResidences
            taxResidences={memberWatch.taxResidences}
            defaultCountry={memberWatch?.country}
            onUpdate={(value: ITaxResidenceCreate[]) => handleChange('taxResidences', value)}
          />
        ) : (
          <div>
            <Button
              variant="ghost"
              icon="add_circle"
              size="s"
              onClick={() => handleChange('taxResidences', [defaultTaxResidence])}
            >
              {!memberWatch?.roles?.length || memberWatch?.roles?.includes(EParentType.leader)
                ? t('pages.currentProfiles.profile.taxResidence.form.addItemOptional')
                : t('pages.currentProfiles.profile.taxResidence.form.addItem')}
            </Button>
          </div>
        )}
      </Section>
    </div>
  );
}
